// Ported verbatim from MailBaser; types erased for Vue 2 / Node 14. See provenance.json.
// Block-based email documents: the drag-and-drop builder edits a JSON
// document of stacked blocks, and this module compiles it to table-based,
// inline-styled HTML that renders safely in email clients. Workspace brand
// footers and compliance links are composed later by the sending pipeline.
const font = "font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,Helvetica,Arial,sans-serif;";
export function createEmailBlocksDocument() {
    return {
        version: 1,
        accentColor: "#4f46e5",
        backgroundColor: "#f4f4f7",
        blocks: [
            {
                id: "block-heading-1",
                type: "heading",
                text: "Your headline here",
                level: 1,
                align: "left",
            },
            {
                id: "block-text-1",
                type: "text",
                text: "Hi {{first_name}},\n\nWrite your message here. Each paragraph becomes its own line in the email.",
                align: "left",
            },
            {
                id: "block-button-1",
                type: "button",
                label: "Call to action",
                url: "https://example.com",
                align: "center",
            },
        ],
    };
}
export function parseEmailBlocksDocument(value) {
    if (!value || typeof value !== "object" || Array.isArray(value)) {
        return null;
    }
    const record = value;
    if (record.version !== 1 || !Array.isArray(record.blocks)) {
        return null;
    }
    return {
        version: 1,
        accentColor: typeof record.accentColor === "string" ? record.accentColor : "#4f46e5",
        backgroundColor: typeof record.backgroundColor === "string"
            ? record.backgroundColor
            : "#f4f4f7",
        blocks: record.blocks.filter(isEmailBlock),
    };
}
function isEmailBlock(value) {
    if (!value || typeof value !== "object") {
        return false;
    }
    const block = value;
    return (typeof block.id === "string" &&
        [
            "hero",
            "heading",
            "text",
            "list",
            "callout",
            "quote",
            "image",
            "video",
            "button",
            "social",
            "divider",
            "spacer",
        ].includes(String(block.type)));
}
function escapeHtml(value) {
    return value
        .replaceAll("&", "&amp;")
        .replaceAll("<", "&lt;")
        .replaceAll(">", "&gt;")
        .replaceAll('"', "&quot;");
}
// Merge variables survive escaping because they contain no markup characters.
// After escaping, a small markdown subset is expanded: **bold** and
// [label](https://url) links, so text blocks can carry inline links safely.
// Email clients cannot play inline video, so video blocks compile to a
// linked thumbnail. YouTube stills are fetchable without an API key.
export function youtubeThumbnailUrl(url) {
    const match = url.match(/(?:youtube\.com\/(?:watch\?v=|shorts\/|embed\/)|youtu\.be\/)([A-Za-z0-9_-]{6,})/);
    return match ? `https://i.ytimg.com/vi/${match[1]}/hqdefault.jpg` : null;
}
export const socialNetworks = [
    ["facebook", "Facebook"],
    ["instagram", "Instagram"],
    ["x", "X"],
    ["linkedin", "LinkedIn"],
    ["youtube", "YouTube"],
    ["tiktok", "TikTok"],
];
function escapeText(value, linkColor = "#4f46e5") {
    return escapeHtml(value)
        .replace(/\*\*([^*]+)\*\*/g, "<strong>$1</strong>")
        .replace(/\[([^\]]+)\]\((https?:\/\/[^\s)]+|#)\)/g, `<a href="$2" style="color:${linkColor};font-weight:bold;text-decoration:none;">$1</a>`);
}
function renderBlock(block, accent) {
    switch (block.type) {
        case "hero": {
            const eyebrow = block.eyebrow
                ? `<p style="margin:0 0 10px;${font}font-size:12px;letter-spacing:2px;text-transform:uppercase;color:rgba(255,255,255,0.78);font-weight:bold;">${escapeText(block.eyebrow, "#ffffff")}</p>`
                : "";
            const subtitle = block.subtitle
                ? `<p style="margin:12px 0 0;${font}font-size:16px;line-height:24px;color:rgba(255,255,255,0.85);">${escapeText(block.subtitle, "#ffffff")}</p>`
                : "";
            return `<tr><td style="background-color:${escapeHtml(block.backgroundColor)};padding:44px 40px;" align="center">${eyebrow}<h1 style="margin:0;${font}font-size:30px;line-height:38px;color:#ffffff;">${escapeText(block.title, "#ffffff")}</h1>${subtitle}</td></tr>`;
        }
        case "heading": {
            const size = block.level === 1 ? "26px" : "20px";
            const lineHeight = block.level === 1 ? "34px" : "28px";
            return `<tr><td style="padding:12px 40px 4px;" align="${block.align}"><h${block.level} style="margin:0;${font}font-size:${size};line-height:${lineHeight};color:#1a1a24;">${escapeText(block.text)}</h${block.level}></td></tr>`;
        }
        case "text": {
            const paragraphs = block.text
                .split(/\n{2,}/)
                .map((paragraph) => paragraph.trim())
                .filter(Boolean)
                .map((paragraph) => `<p style="margin:0 0 14px;${font}font-size:15px;line-height:25px;color:#3c3c46;">${escapeText(paragraph).replaceAll("\n", "<br>")}</p>`)
                .join("");
            return `<tr><td style="padding:8px 40px 0;" align="${block.align}">${paragraphs}</td></tr>`;
        }
        case "list": {
            const marker = (index) => {
                switch (block.style) {
                    case "number":
                        return `<span style="display:inline-block;width:26px;height:26px;border-radius:13px;background-color:${accent};${font}font-size:13px;font-weight:bold;color:#ffffff;text-align:center;line-height:26px;">${index + 1}</span>`;
                    case "check":
                        return `<span style="${font}font-size:15px;font-weight:bold;color:${accent};">&#10003;</span>`;
                    default:
                        return `<span style="${font}font-size:15px;font-weight:bold;color:${accent};">&#8226;</span>`;
                }
            };
            const rows = block.items
                .map((item) => item.trim())
                .filter(Boolean)
                .map((item, index) => `<tr><td style="padding:0 0 14px;${font}font-size:15px;line-height:24px;color:#3c3c46;">${marker(index)}&nbsp;&nbsp;${escapeText(item, accent)}</td></tr>`)
                .join("");
            return `<tr><td style="padding:8px 40px 0;"><table role="presentation" width="100%" cellpadding="0" cellspacing="0" border="0">${rows}</table></td></tr>`;
        }
        case "callout": {
            const border = block.borderStyle === "none"
                ? ""
                : `border:2px ${block.borderStyle} ${escapeHtml(block.borderColor)};`;
            const title = block.title
                ? `<p style="margin:0 0 6px;${font}font-size:12px;letter-spacing:1px;text-transform:uppercase;color:${escapeHtml(block.borderColor)};font-weight:bold;">${escapeText(block.title, block.borderColor)}</p>`
                : "";
            const textStyle = block.textSize === "large"
                ? `${font}font-size:24px;font-weight:bold;letter-spacing:3px;color:${escapeHtml(block.borderColor)};`
                : `${font}font-size:15px;line-height:24px;color:#3c3c46;`;
            const paragraphs = block.text
                .split("\n")
                .map((line) => line.trim())
                .filter(Boolean)
                .map((line, index, lines) => `<p style="margin:0 0 ${index === lines.length - 1 ? 0 : 6}px;${textStyle}">${escapeText(line, block.borderColor)}</p>`)
                .join("");
            return `<tr><td style="padding:12px 40px;"><table role="presentation" width="100%" cellpadding="0" cellspacing="0" border="0"><tr><td align="${block.align}" style="background-color:${escapeHtml(block.backgroundColor)};${border}border-radius:8px;padding:18px 24px;">${title}${paragraphs}</td></tr></table></td></tr>`;
        }
        case "quote": {
            const attribution = block.attribution
                ? `<p style="margin:8px 0 0;${font}font-size:14px;line-height:22px;color:#8a8a99;">&mdash; ${escapeText(block.attribution, accent)}</p>`
                : "";
            return `<tr><td style="padding:12px 40px;"><table role="presentation" width="100%" cellpadding="0" cellspacing="0" border="0"><tr><td style="border-left:3px solid ${escapeHtml(accent)};padding:4px 0 4px 18px;"><p style="margin:0;${font}font-size:17px;line-height:27px;font-style:italic;color:#3c3c46;">&ldquo;${escapeText(block.text, accent)}&rdquo;</p>${attribution}</td></tr></table></td></tr>`;
        }
        case "video": {
            const thumbnail = block.thumbnailUrl || youtubeThumbnailUrl(block.url);
            const href = escapeHtml(block.url || "#");
            const image = thumbnail
                ? `<a href="${href}" style="text-decoration:none;"><img src="${escapeHtml(thumbnail)}" alt="Video preview" width="520" style="display:block;width:100%;max-width:520px;height:auto;border-radius:8px 8px 0 0;"></a>`
                : "";
            const bar = `<a href="${href}" style="display:block;background-color:${escapeHtml(accent)};padding:12px;${font}font-size:15px;font-weight:bold;color:#ffffff;text-decoration:none;text-align:center;border-radius:${thumbnail ? "0 0 8px 8px" : "8px"};">&#9654;&nbsp;&nbsp;${escapeText(block.label || "Watch the video", "#ffffff")}</a>`;
            return `<tr><td style="padding:12px 40px;">${image}${bar}</td></tr>`;
        }
        case "social": {
            const links = socialNetworks
                .map(([key, label]) => [label, block[key]])
                .filter(([, url]) => url.trim())
                .map(([label, url]) => `<a href="${escapeHtml(url)}" style="${font}font-size:14px;font-weight:bold;color:${escapeHtml(accent)};text-decoration:none;">${label}</a>`)
                .join("&nbsp;&nbsp;&middot;&nbsp;&nbsp;");
            return links
                ? `<tr><td align="center" style="padding:14px 40px;">${links}</td></tr>`
                : "";
        }
        case "image": {
            const image = `<img src="${escapeHtml(block.src)}" alt="${escapeHtml(block.alt)}" width="520" style="display:block;width:100%;max-width:520px;height:auto;border-radius:6px;">`;
            const wrapped = block.href
                ? `<a href="${escapeHtml(block.href)}" style="text-decoration:none;">${image}</a>`
                : image;
            return `<tr><td style="padding:12px 40px;" align="center">${wrapped}</td></tr>`;
        }
        case "button":
            return `<tr><td style="padding:16px 40px;" align="${block.align}"><table role="presentation" cellpadding="0" cellspacing="0" border="0" align="${block.align}"><tr><td style="border-radius:6px;background-color:${accent};"><a href="${escapeHtml(block.url)}" style="display:inline-block;padding:13px 32px;${font}font-size:15px;font-weight:bold;color:#ffffff;text-decoration:none;border-radius:6px;">${escapeText(block.label)}</a></td></tr></table></td></tr>`;
        case "divider":
            return `<tr><td style="padding:16px 40px;"><hr style="border:none;border-top:1px solid #e8e8ee;margin:0;"></td></tr>`;
        case "spacer":
            return `<tr><td style="height:${Math.max(4, Math.min(120, block.height))}px;font-size:1px;line-height:1px;">&nbsp;</td></tr>`;
        default:
            return "";
    }
}
export function compileEmailBlocks(document) {
    var _a;
    const rows = document.blocks
        .map((block) => renderBlock(block, document.accentColor))
        .join("\n");
    // Hero banners bleed to the card edge, so the top breathing row is skipped.
    const topSpacer = ((_a = document.blocks[0]) === null || _a === void 0 ? void 0 : _a.type) === "hero"
        ? ""
        : `<tr><td style="height:20px;font-size:1px;line-height:1px;">&nbsp;</td></tr>\n`;
    return `<table role="presentation" width="100%" cellpadding="0" cellspacing="0" border="0" style="background-color:${document.backgroundColor};padding:0;margin:0;"><tr><td align="center" style="padding:32px 12px;">
<table role="presentation" width="600" cellpadding="0" cellspacing="0" border="0" style="max-width:600px;width:100%;background-color:#ffffff;border-radius:10px;overflow:hidden;">
${topSpacer}${rows}
<tr><td style="height:24px;font-size:1px;line-height:1px;">&nbsp;</td></tr>
</table>
</td></tr></table>`;
}
export function emailBlocksToText(document) {
    const lines = document.blocks
        .map((block) => {
        switch (block.type) {
            case "hero":
                return [block.eyebrow, block.title, block.subtitle]
                    .filter(Boolean)
                    .join("\n");
            case "heading":
                return block.text;
            case "text":
                return stripInlineMarkdown(block.text);
            case "list":
                return block.items
                    .map((item) => item.trim())
                    .filter(Boolean)
                    .map((item, index) => `${block.style === "number" ? `${index + 1}.` : "-"} ${stripInlineMarkdown(item)}`)
                    .join("\n");
            case "callout":
                return [block.title, stripInlineMarkdown(block.text)]
                    .filter(Boolean)
                    .join("\n");
            case "quote":
                return [`"${stripInlineMarkdown(block.text)}"`, block.attribution]
                    .filter(Boolean)
                    .join(" — ");
            case "video":
                return block.url ? `${block.label || "Watch the video"}: ${block.url}` : "";
            case "social":
                return socialNetworks
                    .map(([key, label]) => [label, block[key]])
                    .filter(([, url]) => url.trim())
                    .map(([label, url]) => `${label}: ${url}`)
                    .join("\n");
            case "button":
                return `${block.label}: ${block.url}`;
            case "image":
                return block.alt;
            default:
                return "";
        }
    })
        .filter(Boolean);
    return lines.join("\n\n");
}
export function stripInlineMarkdown(value) {
    return value
        .replace(/\*\*([^*]+)\*\*/g, "$1")
        .replace(/\[([^\]]+)\]\((?:https?:\/\/[^\s)]+|#)\)/g, "$1");
}
